import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Lock, ChevronLeft, ArrowRight, AlertCircle, ShieldAlert } from 'lucide-react';

interface AdminAuthPageProps {
  onSuccess: () => void;
  onBackToHome: () => void;
}

export const AdminAuthPage: React.FC<AdminAuthPageProps> = ({
  onSuccess,
  onBackToHome,
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [failCount, setFailCount] = useState(0);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && lockoutSeconds === 0) {
      inputRef.current.focus();
    }
  }, [lockoutSeconds]);

  // Lockout countdown timer
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const timer = setInterval(() => {
      setLockoutSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lockoutSeconds]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (lockoutSeconds > 0) return;
    
    // Configured password: 0313
    if (password.trim() === '0313') {
      setError(false);
      setFailCount(0);
      try {
        sessionStorage.setItem('plus_sd_admin_auth', 'true');
      } catch {
        // storage fallback
      }
      onSuccess();
    } else {
      const nextFail = failCount + 1;
      setFailCount(nextFail);
      setError(true);

      if (nextFail >= 5) {
        setLockoutSeconds(30);
        setErrorMessage('비밀번호를 5회 연속 잘못 입력했습니다. 30초 후 다시 시도해 주세요.');
      } else {
        setErrorMessage(`비밀번호가 일치하지 않습니다. (남은 시도: ${5 - nextFail}회)`);
      }

      setPassword('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-sky-500 selection:text-white flex flex-col justify-between">
      
      {/* Top Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-slate-200 py-3.5 px-4 sm:px-6 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            type="button"
            onClick={onBackToHome}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3.5 py-2 rounded-xl transition-all cursor-pointer shadow-sm"
          >
            <ChevronLeft className="w-4 h-4 text-sky-600" />
            <span>메인 홈으로</span>
          </button>

          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-slate-900 p-1 shadow flex items-center justify-center overflow-hidden shrink-0">
              <img src="/plus_sd_logo.png" alt="+SD" className="w-full h-full object-contain" />
            </div>
            <span className="text-sm font-black text-slate-900">플러스에스디 (+SD)</span>
          </div>

          <div className="w-20" /> {/* Spacer */}
        </div>
      </header>

      {/* Main Password Input Box */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-8 shadow-xl space-y-6 text-center">
          
          {/* Security Icon Badge */}
          <div className="flex justify-center">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-sm ${
              lockoutSeconds > 0
                ? 'bg-rose-50 border border-rose-200 text-rose-600'
                : 'bg-sky-50 border border-sky-200 text-sky-600'
            }`}>
              {lockoutSeconds > 0 ? (
                <ShieldAlert className="w-8 h-8 text-rose-600 animate-pulse" />
              ) : (
                <Lock className="w-8 h-8 text-sky-600" />
              )}
            </div>
          </div>

          {/* Heading */}
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-black">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>관리자 전용 보안 시스템</span>
            </div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">
              접근 비밀번호 입력
            </h1>
            <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
              허가된 사내 임직원 및 현장 담당자만 접근 가능합니다. 4자리 보안 비밀번호를 입력해 주세요.
            </p>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                ref={inputRef}
                type="password"
                maxLength={8}
                disabled={lockoutSeconds > 0}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(false);
                }}
                placeholder={lockoutSeconds > 0 ? `잠금 해제까지 ${lockoutSeconds}초` : '비밀번호 4자리 입력'}
                className={`w-full text-center tracking-[0.4em] text-2xl font-black py-4 px-4 rounded-2xl bg-slate-50 border transition-all focus:outline-none ${
                  lockoutSeconds > 0
                    ? 'bg-rose-50/50 border-rose-300 text-rose-400 cursor-not-allowed text-base tracking-normal'
                    : error
                    ? 'border-rose-500 focus:ring-2 focus:ring-rose-200 text-rose-600'
                    : 'border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 text-slate-900 focus:bg-white'
                }`}
              />

              {error && (
                <div className="flex items-center justify-center gap-1.5 text-xs text-rose-600 font-bold mt-2 animate-in fade-in">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={lockoutSeconds > 0}
              className={`w-full py-4 rounded-2xl text-white font-black text-sm flex items-center justify-center gap-2 shadow-md transition-all active:scale-98 cursor-pointer ${
                lockoutSeconds > 0
                  ? 'bg-slate-400 cursor-not-allowed opacity-60'
                  : 'bg-sky-600 hover:bg-sky-500 shadow-sky-600/25'
              }`}
            >
              <span>{lockoutSeconds > 0 ? `입력 제한 중 (${lockoutSeconds}s)` : '관리자 인증 및 접속'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {/* Quick Help */}
          <div className="pt-2 border-t border-slate-100 text-xs text-slate-400">
            <span>비밀번호 분실 또는 권한 문의: 사내 관리팀</span>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-4 px-6 text-center text-xs text-slate-500">
        <p>© 2026 +SD PLUS SD SOLAR AGENCY • 보안 인증 시스템</p>
      </footer>

    </div>
  );
};
